import { useNavigate } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { CATEGORICAL, chartTheme } from "./chartTheme";
import { ChartCard, EmptyState } from "./ChartCard";
import { useMetricsQuality } from "./useMetricsQueries";
import { useMetricsFilter } from "./MetricsFilterContext";
import { buildPipelineDrillThroughUrl } from "@/lib/pipelineFilterEncoder";
import type {
  DqrDistributionBucket,
  FailuresByLaneBucket,
  FailuresByTierBucket,
  TopFailureType,
} from "@/api/dealFlow/metrics";

// Quality tab (DQR + failures).

export function QualityMetricsSection() {
  const { filter } = useMetricsFilter();
  const query = useMetricsQuality(filter);
  if (query.isLoading) return <Loading />;
  if (query.error || !query.data) return <Error />;
  const { dqrDistribution, failuresByTier, failuresByLane, topFailures } = query.data;

  return (
    <div
      className="grid grid-cols-1 gap-space-4 lg:grid-cols-2"
      data-testid="metrics-loaded"
    >
      <ChartCard title="DQR distribution" hint="Combined Deal Quality Rating across passing outcomes." testId="chart-dqr">
        <DqrChart data={dqrDistribution} />
      </ChartCard>
      <ChartCard title="Failures by tier" hint="DealKiller / RedFlag / NeedsImprovement, plus lane-level." testId="chart-tier">
        <FailuresByTierChart data={failuresByTier} />
      </ChartCard>
      <ChartCard
        title="Failures by lane"
        hint="Click a slice to drill through to deals with that lane's failures."
        testId="chart-lane"
      >
        <FailuresByLaneChart data={failuresByLane} />
      </ChartCard>
      <ChartCard
        title="Top 10 failure types"
        hint="Item-level only — lane-level failures appear above."
        testId="chart-top-failures"
      >
        <TopFailuresChart data={topFailures} />
      </ChartCard>
    </div>
  );
}

function DqrChart({ data }: { data: DqrDistributionBucket[] }) {
  if (data.every((b) => b.count === 0)) return <EmptyState label="No finalized outcomes in this window." />;
  return (
    <ResponsiveContainer width="100%" height={280}>
      <BarChart data={data} margin={{ left: 8, right: 8 }}>
        <CartesianGrid stroke={chartTheme.grid} strokeDasharray="3 3" />
        <XAxis dataKey="label" stroke={chartTheme.axis} fontSize={11} />
        <YAxis stroke={chartTheme.axis} />
        <Tooltip />
        <Bar dataKey="count">
          {data.map((d, i) => (
            <Cell key={d.combinedDqr} fill={CATEGORICAL[i % CATEGORICAL.length]} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function FailuresByTierChart({ data }: { data: FailuresByTierBucket[] }) {
  if (data.length === 0) return <EmptyState label="No failures in this window." />;
  return (
    <ResponsiveContainer width="100%" height={280}>
      <PieChart>
        <Pie
          data={data}
          dataKey="count"
          nameKey="label"
          innerRadius={50}
          outerRadius={100}
          paddingAngle={2}
        >
          {data.map((d, i) => (
            <Cell key={d.label} fill={CATEGORICAL[i % CATEGORICAL.length]} />
          ))}
        </Pie>
        <Tooltip />
      </PieChart>
    </ResponsiveContainer>
  );
}

function FailuresByLaneChart({ data }: { data: FailuresByLaneBucket[] }) {
  const navigate = useNavigate();
  if (data.length === 0) return <EmptyState label="No failures in this window." />;
  return (
    <ResponsiveContainer width="100%" height={280}>
      <PieChart>
        <Pie
          data={data}
          dataKey="count"
          nameKey="label"
          innerRadius={50}
          outerRadius={100}
          paddingAngle={2}
          onClick={(slice) => {
            const lane = (slice as { payload: FailuresByLaneBucket }).payload?.lane;
            if (lane) {
              navigate(
                buildPipelineDrillThroughUrl({
                  failureLane: lane,
                  failureStatus: "Open",
                }),
              );
            }
          }}
        >
          {data.map((d, i) => (
            <Cell key={d.lane} fill={CATEGORICAL[i % CATEGORICAL.length]} cursor="pointer" />
          ))}
        </Pie>
        <Tooltip />
      </PieChart>
    </ResponsiveContainer>
  );
}

function TopFailuresChart({ data }: { data: TopFailureType[] }) {
  const navigate = useNavigate();
  if (data.length === 0) return <EmptyState label="No item-level failures in this window." />;
  return (
    <ResponsiveContainer width="100%" height={320}>
      <BarChart data={data} layout="vertical" margin={{ left: 16, right: 16 }}>
        <CartesianGrid stroke={chartTheme.grid} strokeDasharray="3 3" />
        <XAxis type="number" stroke={chartTheme.axis} />
        <YAxis
          dataKey="code"
          type="category"
          width={140}
          stroke={chartTheme.axis}
          fontSize={11}
        />
        <Tooltip />
        <Bar
          dataKey="count"
          cursor="pointer"
          onClick={(entry) => {
            const code = (entry as unknown as TopFailureType).code;
            navigate(buildPipelineDrillThroughUrl({ failureCode: code }));
          }}
        >
          {data.map((d) => (
            <Cell key={d.code} fill={chartTheme.primary} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function Loading() {
  return (
    <div className="flex h-64 items-center justify-center text-small text-gray-mid">
      Loading quality metrics…
    </div>
  );
}
function Error() {
  return (
    <div className="flex h-64 items-center justify-center text-small text-status-error">
      Failed to load quality metrics.
    </div>
  );
}
